/**
 * JourneyGateScreen — gate de fase genérico (Tecnologia/Escopo/Final). Bloco 2.
 *
 * Recebe `gateKey` (tecnologia|escopo|final) + `title`. Lê ?project=, mostra o
 * status atual do gate e permite Aprovar / Rejeitar / Waiver (risco aceito) via
 * PATCH /projects/{id}/journey-gates/{gateKey}. Auditável no backend.
 */
import { C } from '@/theme/palette';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProjects } from '@/api/projects';
import { useJourneyGate, useDecideJourneyGate, useTechBlueprint, useArtifacts } from '@/api/techScope';
import { useBacklog } from '@/api/backlog';
import { useDeliveryCheck } from '@/api/deliveryCheck';

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).

const ST = {
  approved: { label: 'Aprovado', fg: C.ok, bg: 'rgba(1,230,228,0.12)' },
  rejected: { label: 'Rejeitado', fg: C.bad, bg: 'rgba(255,51,0,0.12)' },
  pending:  { label: 'Pendente', fg: C.warn, bg: 'rgba(255,176,32,0.12)' },
};

function Card({ title, children }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, padding: 16, marginTop: 12 }}>
      <h4 style={{ margin: '0 0 8px', fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase',
        letterSpacing: '0.1em', color: C.accent }}>{title}</h4>
      {children}
    </div>
  );
}

/** Gate 2 — Tecnologia: blueprint + artefatos gerados. */
function TecReview({ projectId }) {
  const { data: bp } = useTechBlueprint(projectId);
  const { data: arts } = useArtifacts(projectId);
  const langs = bp?.languages ?? []; const fw = bp?.frameworks ?? [];
  const dbs = bp?.databases ?? []; const artifacts = arts?.artifacts ?? [];
  if (!langs.length && !artifacts.length) return null;
  return (
    <>
      {(bp?.targetArchitecture || langs.length) && (
        <Card title="Blueprint (stack-alvo)">
          {bp?.targetArchitecture && <p style={{ margin: '0 0 8px', color: C.text, fontSize: 13 }}>{bp.targetArchitecture}</p>}
          <div style={{ fontSize: 12.5, color: C.dim }}>
            <b style={{ color: C.text }}>Linguagens:</b> {langs.join(', ') || '—'} · <b style={{ color: C.text }}>Frameworks:</b> {fw.slice(0, 4).join(', ') || '—'} · <b style={{ color: C.text }}>Bancos:</b> {dbs.join(', ') || '—'}
          </div>
        </Card>
      )}
      {artifacts.length > 0 && (
        <Card title={`Artefatos gerados (${artifacts.length})`}>
          <ul style={{ margin: 0, paddingLeft: 16, color: C.dim, fontSize: 12.5 }}>
            {artifacts.map((a) => <li key={a.id}>[{a.kind}] {a.title}</li>)}
          </ul>
        </Card>
      )}
    </>
  );
}

/** Gate 4 — Escopo: épicos + ciclos. */
function EscopoReview({ projectId }) {
  const { data } = useBacklog(projectId);
  const epics = data?.epics ?? []; const cycles = data?.cycles ?? [];
  if (!epics.length && !cycles.length) return null;
  return (
    <>
      {cycles.length > 0 && (
        <Card title={`Ciclos (${cycles.length})`}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {cycles.map((c) => <span key={c.number} style={{ fontFamily: 'monospace', fontSize: 11,
              padding: '3px 9px', borderRadius: 6, background: C.surface2, color: C.accent }}>Ciclo {c.number}: {c.theme}</span>)}
          </div>
        </Card>
      )}
      {epics.length > 0 && (
        <Card title={`Backlog — épicos (${epics.length})`}>
          <ul style={{ margin: 0, paddingLeft: 16, color: C.dim, fontSize: 12.5 }}>
            {epics.map((e) => <li key={e.id}>{e.title} <span style={{ color: C.muted }}>· {e.features.length} features</span></li>)}
          </ul>
        </Card>
      )}
    </>
  );
}

/** Gate 5 — Final: rastreabilidade (veredito). */
function FinalReview({ projectId }) {
  const { data } = useDeliveryCheck(projectId);
  const rows = data?.rows ?? [];
  if (!rows.length) return null;
  const ok = rows.filter((r) => r.status === 'atendido').length;
  return (
    <Card title={`Conferência de Entrega — veredito ${data?.verdictPct ?? 0}%`}>
      <div style={{ fontSize: 12.5, color: C.dim, marginBottom: 6 }}>
        {ok}/{rows.length} requisitos atendidos.
      </div>
      <ul style={{ margin: 0, paddingLeft: 16, color: C.dim, fontSize: 12.5 }}>
        {rows.slice(0, 8).map((r, i) => <li key={i}>[{r.status}] {r.requirement}</li>)}
      </ul>
    </Card>
  );
}

function GateReview({ gateKey, projectId }) {
  if (gateKey === 'tecnologia') return <TecReview projectId={projectId} />;
  if (gateKey === 'escopo') return <EscopoReview projectId={projectId} />;
  if (gateKey === 'final') return <FinalReview projectId={projectId} />;
  return null;
}

export function JourneyGateScreen({ gateKey, title }) {
  const [searchParams] = useSearchParams();
  const urlProject = searchParams.get('project');
  const { data: projectsRaw } = useProjects();
  const projects = useMemo(
    () => (Array.isArray(projectsRaw) ? projectsRaw : projectsRaw?.items ?? []),
    [projectsRaw],
  );
  const projectId = urlProject || projects?.[0]?.id || null;
  const { data: gate } = useJourneyGate(projectId, gateKey);
  const decide = useDecideJourneyGate(projectId, gateKey);
  const [comment, setComment] = useState('');
  const [waiverReason, setWaiverReason] = useState('');

  if (!projectId) return <div style={{ padding: 40, color: C.dim }}>Nenhum projeto selecionado.</div>;

  const status = gate?.status ?? 'pending';
  const s = ST[status] || ST.pending;

  return (
    <div className="sp-page" style={{ color: C.text, maxWidth: 720 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.02em' }}>{title}</h2>
        <span style={{ fontFamily: 'monospace', fontSize: 11, padding: '3px 9px', borderRadius: 6,
          color: s.fg, background: s.bg }}>{s.label}</span>
        {gate?.waiver && <span style={{ fontFamily: 'monospace', fontSize: 10, color: C.warn }}>waiver</span>}
      </div>

      {gate?.decidedAt && (
        <p style={{ color: C.muted, fontSize: 12.5, marginTop: 8 }}>
          Decidido em {new Date(gate.decidedAt).toLocaleString('pt-BR')}
          {gate.comment ? ` · ${gate.comment}` : ''}
          {gate.waiverReason ? ` · waiver: ${gate.waiverReason}` : ''}
        </p>
      )}

      {decide.isError && (
        <div style={{ marginTop: 12, color: C.bad, fontSize: 13 }}>
          {String(decide.error?.message || decide.error)}
        </div>
      )}

      {/* Conteúdo de review do gate (o que está sendo aprovado) */}
      <GateReview gateKey={gateKey} projectId={projectId} />

      <div style={{ marginTop: 20, border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, padding: 16 }}>
        <label style={{ fontSize: 12.5, color: C.dim }}>Comentário (obrigatório para rejeitar)</label>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
          style={{ width: '100%', marginTop: 6, background: C.surface2, color: C.text,
            border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, fontSize: 13, resize: 'vertical' }} />

        <label style={{ fontSize: 12.5, color: C.dim, marginTop: 12, display: 'block' }}>
          Justificativa do waiver (risco aceito — obrigatória para waiver)
        </label>
        <textarea value={waiverReason} onChange={(e) => setWaiverReason(e.target.value)} rows={2}
          style={{ width: '100%', marginTop: 6, background: C.surface2, color: C.text,
            border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, fontSize: 13, resize: 'vertical' }} />

        {gateKey === 'final' && (
          <div style={{ marginTop: 4, marginBottom: 6, fontSize: 12.5, color: C.dim }}>
            Aceite <strong style={{ color: C.text }}>duplo</strong>: Negócio (PO) + Técnica (Arq. Soluções).
            {' '}Aceito por Negócio: <b style={{ color: gate?.accepts?.business ? C.ok : C.muted }}>{gate?.accepts?.business ? '✓' : '—'}</b>
            {' '}· Técnica: <b style={{ color: gate?.accepts?.technical ? C.ok : C.muted }}>{gate?.accepts?.technical ? '✓' : '—'}</b>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          {gateKey === 'final' ? (
            <>
              <button onClick={() => decide.mutate({ decision: 'approve', role: 'business', comment })}
                disabled={decide.isPending}
                style={{ background: C.accent, color: C.black, border: 'none', borderRadius: 8,
                  padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Aceitar (Negócio)</button>
              <button onClick={() => decide.mutate({ decision: 'approve', role: 'technical', comment })}
                disabled={decide.isPending}
                style={{ background: C.accent, color: C.black, border: 'none', borderRadius: 8,
                  padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Aceitar (Técnica)</button>
            </>
          ) : (
            <button onClick={() => decide.mutate({ decision: 'approve', comment })}
              disabled={decide.isPending}
              style={{ background: C.accent, color: C.black, border: 'none', borderRadius: 8,
                padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Aprovar</button>
          )}
          <button onClick={() => decide.mutate({ decision: 'reject', comment })}
            disabled={decide.isPending}
            style={{ background: 'transparent', color: C.bad, border: `1px solid ${C.bad}`, borderRadius: 8,
              padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Rejeitar</button>
          <button onClick={() => decide.mutate({ decision: 'waiver', waiver_reason: waiverReason })}
            disabled={decide.isPending}
            style={{ background: 'transparent', color: C.warn, border: `1px solid ${C.warn}`, borderRadius: 8,
              padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Waiver (risco aceito)</button>
        </div>
      </div>
    </div>
  );
}
